import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getAccessToken,
  getTokenExpirationTime,
  refreshAccessToken,
  clearAccessToken,
  clearRefreshToken,
} from 'utils/auth/auth';
import { logout } from 'config/redux/slices/userSlice';

/**
 * Custom hook to automatically refresh access tokens before they expire
 *
 * This hook:
 * 1. Monitors the current access token's expiration time
 * 2. Automatically refreshes the token 2 minutes before expiry
 * 3. Handles refresh failures by logging out the user
 * 4. Cleans up timers on unmount
 *
 * @param isLoggedIn - Boolean indicating if user is logged in
 */
export const useTokenRefresh = (isLoggedIn: boolean) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    clearAccessToken();
    clearRefreshToken();
    navigate('/login');
  }, [dispatch, navigate]);

  const scheduleTokenRefresh = useCallback(
    (token: string) => {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const expirationTime = getTokenExpirationTime(token);
      if (!expirationTime) {
        return;
      }

      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // Refresh 30 seconds before expiry (works better for short-lived tokens)
      // If token expires in less than 30 seconds, refresh immediately
      const refreshBuffer = 30 * 1000; // 30 seconds in milliseconds
      const refreshIn = Math.max(0, timeUntilExpiry - refreshBuffer);

      refreshTimerRef.current = setTimeout(async () => {
        if (isRefreshingRef.current) {
          return;
        }

        try {
          isRefreshingRef.current = true;
          const newToken = await refreshAccessToken(dispatch);

          if (newToken) {
            scheduleTokenRefresh(newToken);
          } else {
            handleLogout();
          }
        } catch {
          handleLogout();
        } finally {
          isRefreshingRef.current = false;
        }
      }, refreshIn);
    },
    [dispatch, handleLogout]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    // Schedule the first refresh
    scheduleTokenRefresh(token);

    // Cleanup function
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isLoggedIn, scheduleTokenRefresh]);

  return null;
};
